import {
  Check,
  ExternalLink,
  RefreshIcon,
  SparklesIcon,
  Warning,
} from 'assets/icons';
import Button from 'components/button';
import { ORANGE_COLORS } from 'utils/colors';
import {
  AI_GENERATION_STEPS,
  GENERATION_STEP_STATE,
  countDoneGenerationSteps,
  getGenerationStepState,
} from './qualificationAiSuggestion';
import { qualificationText } from './qualificationTexts';

const aiText = (key, values) => qualificationText(`ai.${key}`, values);

const WARNING_TEXT_COLOR = '#975612'; // yellow-700
const DARK_ICON_COLOR = '#374151'; // gray-700

const warningBoxClass =
  'rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-700';

export const AiBadge = () => (
  <span className="ml-2 inline-flex shrink-0 items-center rounded border border-yellow-200 bg-yellow-50 px-1.5 py-0.5 text-xs font-medium text-yellow-700">
    {aiText('badge')}
  </span>
);

/** What the AI fills from the title, listed on the first step. */
export const AiFieldPills = () => (
  <ul className="flex flex-wrap gap-2">
    {AI_GENERATION_STEPS.map((step) => (
      <li
        key={step.id}
        className="rounded border border-yellow-300 bg-yellow-50 px-1.5 py-0.5 text-xs text-yellow-700"
      >
        {aiText(`steps.${step.id}`)}
      </li>
    ))}
  </ul>
);

const tasksCountText = (count) => {
  if (count === 0) return qualificationText('list.tasksCountZero');
  return qualificationText(
    count === 1 ? 'list.tasksCountOne' : 'list.tasksCountOther',
    { count }
  );
};

/** The value a finished step brought, when the api already sent it. */
const stepValue = (stepId, progress, sectorLabel) => {
  if (!progress) return '';
  if (stepId === 'sector') {
    const type = progress.sector?.type;
    return type ? sectorLabel(type) : progress.sector?.label ?? '';
  }
  if (stepId === 'tasks') {
    return Array.isArray(progress.tasks)
      ? tasksCountText(progress.tasks.length)
      : '';
  }
  if (stepId === 'codes') {
    return [
      progress.rome?.code && aiText('stepRome', { code: progress.rome.code }),
      progress.pcs?.code && aiText('stepPcs', { code: progress.pcs.code }),
    ]
      .filter(Boolean)
      .join(' · ');
  }
  return '';
};

const STEP_STATE_TEXT = {
  [GENERATION_STEP_STATE.DONE]: 'stepDone',
  [GENERATION_STEP_STATE.IN_PROGRESS]: 'stepPending',
  [GENERATION_STEP_STATE.WAITING]: 'stepWaiting',
};

const StepIcon = ({ state }) => {
  if (state === GENERATION_STEP_STATE.DONE) {
    return (
      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-green-600">
        <Check wh={12} color="#FFFFFF" />
      </span>
    );
  }
  if (state === GENERATION_STEP_STATE.IN_PROGRESS) {
    return (
      <span className="flex h-4 w-4 items-center justify-center animate-spin motion-reduce:animate-none">
        <RefreshIcon wh={16} color={DARK_ICON_COLOR} />
      </span>
    );
  }
  return <span className="h-4 w-4 rounded-full border-2 border-gray-300" />;
};

/**
 * The four steps of the generation screen, in the order of the mock-up, each
 * following the status the api reports. `progress` is the last answer read,
 * for the values a finished step can already show.
 */
export const AiGenerationSteps = ({ status, progress, sectorLabel }) => {
  const doneCount = countDoneGenerationSteps(status);
  const total = AI_GENERATION_STEPS.length;
  return (
    <div className="flex flex-col gap-3 rounded-lg bg-yellow-50 p-4">
      <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
        <SparklesIcon wh={18} color={ORANGE_COLORS[500]} />
        {aiText('generatingTitle')}
      </div>
      <div
        role="progressbar"
        aria-label={aiText('progressLabel')}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={doneCount}
        className="h-1.5 w-full overflow-hidden rounded-full bg-yellow-100"
      >
        <div
          className="h-full rounded-full bg-yellow-500 transition-all duration-500"
          style={{ width: `${(doneCount / total) * 100}%` }}
        />
      </div>
      <ul className="flex flex-col gap-2" aria-label={aiText('generatingTitle')}>
        {AI_GENERATION_STEPS.map((step) => {
          const state = getGenerationStepState(step, status);
          const isDone = state === GENERATION_STEP_STATE.DONE;
          const value = isDone ? stepValue(step.id, progress, sectorLabel) : '';
          return (
            <li
              key={step.id}
              data-testid={`ai-step-${step.id}`}
              data-done={isDone ? 'true' : 'false'}
              data-state={state}
              className="flex items-center gap-3 text-sm"
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                <StepIcon state={state} />
              </span>
              <span
                className={
                  state === GENERATION_STEP_STATE.WAITING
                    ? 'text-gray-400'
                    : 'font-medium text-gray-900'
                }
              >
                {aiText(`steps.${step.id}`)}
              </span>
              {value && <span className="text-gray-500">{value}</span>}
              <span className="sr-only">{aiText(STEP_STATE_TEXT[state])}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const AiPrefilledBanner = ({ onRegenerate, disabled }) => (
  <div className="mb-4 flex items-center justify-between gap-3 rounded-lg bg-yellow-50 px-4 py-3 text-sm text-yellow-700">
    <span>{aiText('prefilled')}</span>
    <Button
      color={disabled ? 'DISABLED' : 'PRIMARY'}
      size="SM"
      className="shrink-0"
      onClick={onRegenerate}
      disabled={disabled}
    >
      <SparklesIcon wh={16} color={disabled ? '#9CA3AF' : '#FFFFFF'} />
      {aiText('regenerate')}
    </Button>
  </div>
);

export const AiFailureMessage = () => (
  <div
    role="alert"
    className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
  >
    {aiText('failed')}
  </div>
);

/** Low confidence on a code: a warning, each alternative applying its code on click. */
export const LowConfidenceAlert = ({ field, alternatives, onPick }) => (
  <div
    role="note"
    data-testid={`ai-low-confidence-${field}`}
    className={warningBoxClass}
  >
    <div className="flex items-start gap-2">
      <span className="mt-0.5 shrink-0">
        <Warning wh={16} color={WARNING_TEXT_COLOR} />
      </span>
      <span>{aiText(`lowConfidence.${field}`)}</span>
    </div>
    {alternatives.length > 0 && (
      <div className="mt-2 flex flex-wrap gap-2">
        {alternatives.map((alternative) => (
          <Button
            key={alternative.code}
            color="SECONDARY"
            size="SM"
            className="text-left"
            onClick={() => onPick(alternative)}
          >
            {aiText('alternative', {
              code: alternative.code,
              label: alternative.label ?? '',
            })}
          </Button>
        ))}
      </div>
    )}
  </div>
);

const similarQualificationHref = (name) =>
  `/qualification?search=${encodeURIComponent(name)}`;

/** Existing qualifications close to the proposal; the AI decides nothing here, the api lists them. */
export const SimilarQualificationsAlert = ({ qualifications, sectorLabel }) => (
  <div
    role="note"
    data-testid="ai-similar-qualifications"
    className={warningBoxClass}
  >
    <div className="flex items-start gap-2">
      <span className="mt-0.5 shrink-0">
        <Warning wh={16} color={WARNING_TEXT_COLOR} />
      </span>
      <span>{aiText('similarTitle')}</span>
    </div>
    <ul className="mt-2 flex flex-col gap-1">
      {qualifications.map((qualification) => (
        <li
          key={qualification.id ?? qualification.name}
          className="flex flex-wrap items-center gap-2"
        >
          <a
            href={similarQualificationHref(qualification.name)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-medium text-yellow-800 underline"
          >
            {qualification.name}
            <ExternalLink wh={12} color="#6B3D0A" />
          </a>
          {qualification.type && (
            <span className="text-yellow-700">
              {sectorLabel(qualification.type)}
            </span>
          )}
          {qualification.isActive === false && (
            <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
              {aiText('similarDisabled')}
            </span>
          )}
        </li>
      ))}
    </ul>
  </div>
);
